import medicationImageUrl from "@/assets/medication.svg"
import BackButton from "@/components/BackButton"
import { Button } from "@/components/chadcn/Button"
import HeroImage from "@/components/HeroImage"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import { useSelfQuery, useUpdatePatientProfileMutation } from "@/data/queries"
import AddMedication from "@/screens/onboarding/components/AddMedication"
import { useState } from "react"

type AddMedicationStepProps = {
  onNext: () => void
}

type Medication = { name: string }

type AddMedicationFormProps = {
  patientId: string
  initialMedications: Medication[]
  onNext: () => void
}

// Split out so `initialMedications` seeds useState on mount only — later
// useSelfQuery refetches must not clobber in-progress edits.
const AddMedicationForm = ({ patientId, initialMedications, onNext }: AddMedicationFormProps) => {
  const { mutateAsync: updateProfile, isPending } = useUpdatePatientProfileMutation()

  const [medications, setMedications] = useState<Medication[]>(initialMedications)

  const handleSubmit = async () => {
    await updateProfile({
      patientId,
      profile: { medications },
    })
    onNext()
  }

  return (
    <div className="flex min-h-[calc(100vh-40vh-56px)] flex-col justify-between gap-6">
      <div className="flex flex-col gap-6">
        <p className="text-base text-primary-contrast">What medication(s) are you taking?</p>
        <AddMedication
          medications={medications}
          onChange={setMedications}
          placeholder="Enter your medications here"
        />
      </div>

      <Button
        type="button"
        disabled={isPending || medications.length === 0}
        className="flex w-full gap-3 py-2 text-base"
        onClick={handleSubmit}
      >
        {isPending && <Spinner />}
        Save and continue
      </Button>
    </div>
  )
}

const AddMedicationStep = ({ onNext }: AddMedicationStepProps) => {
  const { data: self } = useSelfQuery()

  return (
    <Page showAppbar={false}>
      <div className="absolute left-4 top-4 z-10">
        <BackButton />
      </div>
      <HeroImage src={medicationImageUrl} alt="Medication" />
      {self === undefined ? (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      ) : (
        <AddMedicationForm
          patientId={self.id}
          initialMedications={(self.medications ?? []).map((medication) => ({
            name: medication.name,
          }))}
          onNext={onNext}
        />
      )}
    </Page>
  )
}

export default AddMedicationStep
